// Iter 204i — Terminal 2.0 design-mode context.
//
// Persists user's preferred design under localStorage key
// `nd_design_mode` (values: "classic" | "terminal").  Mounts a
// `.terminal-mode` class on <html> so CSS overrides can re-skin every
// existing page without rewriting each one.
//
// Both modes inherit the existing day ⇄ night theme toggle (handled
// separately by ThemeProvider).
import { createContext, useCallback, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "nd_design_mode";
const DesignModeContext = createContext(null);

function readInitial() {
  if (typeof window === "undefined") return "classic";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "terminal" ? "terminal" : "classic";
  } catch {
    return "classic";
  }
}

export function DesignModeProvider({ children }) {
  const [mode, setModeState] = useState(readInitial);

  // Sync class on <html> so CSS overrides can target `.terminal-mode body`.
  useEffect(() => {
    const root = document.documentElement;
    if (mode === "terminal") root.classList.add("terminal-mode");
    else root.classList.remove("terminal-mode");
  }, [mode]);

  const setMode = useCallback((next) => {
    const val = next === "terminal" ? "terminal" : "classic";
    setModeState(val);
    try { window.localStorage.setItem(STORAGE_KEY, val); } catch { /* ignore */ }
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === "terminal" ? "classic" : "terminal");
  }, [mode, setMode]);

  return (
    <DesignModeContext.Provider value={{ mode, setMode, toggle }}>
      {children}
    </DesignModeContext.Provider>
  );
}

export function useDesignMode() {
  const ctx = useContext(DesignModeContext);
  if (!ctx) {
    // Safe fallback so components don't crash if mounted outside the
    // provider (e.g. in isolated tests).
    return { mode: "classic", setMode: () => {}, toggle: () => {} };
  }
  return ctx;
}
